import React, { useState, useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, Check, Users, ListChecks, ClipboardCheck, FileText, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/forms/Field";
import { PROGRAMS } from "@/data/site";

export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const p = PROGRAMS[slug];
  if (!p) return <Navigate to="/loan-programs" replace />;

  const blocks = [
    { title: "Who It's For", icon: Users, items: p.whoFor },
    { title: "Typical Use Cases", icon: ListChecks, items: p.useCases },
    { title: "What We Evaluate", icon: ClipboardCheck, items: p.evaluate },
    { title: "Documents Commonly Required", icon: FileText, items: p.documents },
  ];

  return (
    <div>
      <section className="relative border-b border-edge overflow-hidden">
        <div className="absolute inset-0">
          <img src={p.image} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/95 to-obsidian/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="eyebrow mb-3">{p.title}</div>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl">{p.headline}</h1>
          <p className="mt-5 text-base sm:text-lg text-dim max-w-2xl leading-relaxed">{p.description}</p>
          <Button onClick={() => navigate(`/apply?type=${p.ctaType}`)} data-testid="program-cta" className="btn-gold rounded-full h-13 px-8 mt-8 gap-2 h-14">
            {p.cta} <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {blocks.map((b) => (
            <div key={b.title} className="card-rich p-7" data-testid={`program-block-${b.title.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 icon-chip shrink-0"><b.icon className="w-5 h-5 text-gold" strokeWidth={1.7} /></div>
                <h2 className="text-xl font-bold text-white">{b.title}</h2>
              </div>
              <ul className="mt-4 space-y-2.5">
                {b.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-dim">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {p.calculator && <DSCRCalculator />}

        <div className="mt-14 rounded-2xl border border-gold/30 bg-gold/5 p-8 sm:p-10 text-center" data-testid="program-bottom-cta">
          <h3 className="h-display text-2xl sm:text-3xl text-white">Ready to move on this deal?</h3>
          <p className="mt-2 text-dim">Submit the transaction and a BullsEye Capital representative will follow up.</p>
          <Button onClick={() => navigate(`/apply?type=${p.ctaType}`)} className="btn-gold rounded-full h-14 px-8 mt-6 gap-2">{p.cta} <ArrowRight className="w-5 h-5" /></Button>
        </div>
      </section>
    </div>
  );
}

function DSCRCalculator() {
  const [v, setV] = useState({ rent: "", principal: "", interest: "", taxes: "", insurance: "", hoa: "" });
  const set = (n, val) => setV((s) => ({ ...s, [n]: val }));
  const num = (x) => parseFloat(x) || 0;

  const { dscr, debt } = useMemo(() => {
    const debt = num(v.principal) + num(v.interest) + num(v.taxes) + num(v.insurance) + num(v.hoa);
    const dscr = debt > 0 ? num(v.rent) / debt : 0;
    return { dscr, debt };
  }, [v]);

  const verdict = dscr >= 1.25 ? { t: "Strong", c: "text-ok" } : dscr >= 1.0 ? { t: "Review", c: "text-warn" } : dscr > 0 ? { t: "Below 1.0x", c: "text-brandred" } : { t: "—", c: "text-faint" };

  return (
    <div className="mt-14 card-rich p-7 sm:p-10" data-testid="dscr-calculator">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 icon-chip shrink-0"><Calculator className="w-5 h-5 text-gold" strokeWidth={1.7} /></div>
        <h2 className="h-display text-2xl text-white">DSCR Calculator</h2>
      </div>
      <p className="mt-2 text-sm text-dim">Estimate the Debt Service Coverage Ratio (DSCR). Enter monthly figures.</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <TextField label="Monthly Rent" name="rent" value={v.rent} onChange={set} type="number" testId="dscr-rent" />
        <TextField label="Principal (monthly)" name="principal" value={v.principal} onChange={set} type="number" testId="dscr-principal" />
        <TextField label="Interest (monthly)" name="interest" value={v.interest} onChange={set} type="number" testId="dscr-interest" />
        <TextField label="Taxes (monthly)" name="taxes" value={v.taxes} onChange={set} type="number" testId="dscr-taxes" />
        <TextField label="Insurance (monthly)" name="insurance" value={v.insurance} onChange={set} type="number" testId="dscr-insurance" />
        <TextField label="HOA (monthly)" name="hoa" value={v.hoa} onChange={set} type="number" testId="dscr-hoa" />
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-edge bg-surface/60 p-5">
          <div className="eyebrow">Monthly Debt Service</div>
          <div className="font-mono text-2xl text-white mt-1">${debt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="rounded-xl border border-gold/30 bg-gold/5 p-5">
          <div className="eyebrow">Estimated DSCR</div>
          <div className="font-mono text-2xl text-gold mt-1" data-testid="dscr-result">{dscr ? dscr.toFixed(2) + "x" : "—"}</div>
        </div>
        <div className="rounded-xl border border-edge bg-surface/60 p-5">
          <div className="eyebrow">Indicator</div>
          <div className={`font-mono text-2xl mt-1 ${verdict.c}`}>{verdict.t}</div>
        </div>
      </div>
      <p className="mt-4 text-xs text-faint">Results are estimates only and do not represent an approval, commitment, or offer of financing.</p>
    </div>
  );
}
